import { useEffect, useRef } from 'react';
import { authApi } from '@/services/api';
import { getAuthToken, isTokenExpiringSoon, getTokenExpiry } from '@/services/api';
import { clearAuthData } from '@/services/api';

/**
 * Hook to automatically refresh token before expiration
 * Checks token expiry every minute and refreshes if expiring within 5 minutes
 */
export function useTokenRefresh() {
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  useEffect(() => {
    // Only set up refresh if user is authenticated
    if (!getAuthToken()) {
      return;
    }

    const checkAndRefreshToken = async () => {
      // Prevent multiple simultaneous refresh attempts
      if (isRefreshingRef.current) {
        return;
      }

      // Check if token is expiring soon (within 5 minutes)
      if (isTokenExpiringSoon(5)) {
        const expiry = getTokenExpiry();
        
        // If token is already expired, clear auth data
        if (expiry && Date.now() >= expiry) {
          console.warn('Token has expired, clearing auth data');
          clearAuthData();
          window.location.href = '/login';
          return;
        }

        // Refresh token if expiring soon
        try {
          isRefreshingRef.current = true;
          await authApi.refresh();
          console.log('Token refreshed successfully');
        } catch (error) {
          console.error('Token refresh failed:', error);
          // If refresh fails, token might be invalid - clear auth and redirect
          clearAuthData();
          window.location.href = '/login';
        } finally {
          isRefreshingRef.current = false;
        }
      }
    };

    // Check immediately
    checkAndRefreshToken();

    // Check every minute (60000 ms)
    refreshIntervalRef.current = setInterval(checkAndRefreshToken, 60 * 1000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []); // Only run once on mount
}


